import React, { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import capitalize from 'lodash/capitalize'
import find from 'lodash/find'
import { UNASSIGNED_VALUE } from 'transfix-ui/components/Autosuggest'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage } from 'transfix-ui/components/InlineMessage'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import { useDebouncedCallback } from 'use-debounce'
import { useValidationFormContext } from 'utils/context/ValidationFormContext'
import { useRefresh } from 'utils/hooks/useRefresh'
import { DriverAutosuggest } from 'apps/fleet-planner/components/atoms'
import { DriverForm } from 'apps/fleet-planner/components/forms/DriverForm'
import { AddEntityModal } from 'apps/fleet-planner/components/modals/AddEntityModal'
import { FP_SUPPORT_EMAIL } from 'apps/fleet-planner/config'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useTeamMembersApi } from 'apps/fleet-planner/hooks/useTeamMembersApi'
import { createDriverRequest, DriverSchemaType } from 'apps/fleet-planner/schema/DriverSchema'
import {
  ICarrier,
  IDriver,
  ILoad,
  ITeamMember,
  TEAM_MEMBER_ROLE_TYPES,
  TEAM_MEMBER_USER_TYPES,
} from 'apps/fleet-planner/types'
import { fetchDriversSuccess } from 'apps/fleet-planner/utils/actions/drivers'
import * as Api from 'apps/fleet-planner/utils/api'
import { driverStateSelector, selectCarriersList } from 'apps/fleet-planner/utils/selectors'
import { Toast, useToastState } from 'transfix-ui'

interface IDriverProps {
  value: Partial<ILoad>
  /**
   * Trigger a dispatch action to fetch drivers
   */
  refetchOptions: boolean
  errors?: string
  /**
   * @default 'vertical'
   */
  layout?: 'horizontal' | 'vertical'
  /**
   * @default true
   */
  large?: boolean
  /**
   * @default true
   */
  required?: boolean
  /**
   * Groups results into two sections based on
   * whether the driver is part of current carrier
   * @default false
   */
  filterByCarrier?: boolean
  /**
   * When state changes, update the parent component
   */
  onChange: (change: Partial<ILoad>) => void
  /**
   * @default true
   */
  isAddEntityEnabled?: boolean
  /**
   * Set a default value for the form
   */
  selectedMotorCarrier?: ICarrier
  /**
   * @default false
   */
  isExternal?: boolean
}

const defaultNewDriverState = {
  firstName: '',
  lastName: '',
  email: '',
  cellPhone: '',
  motorCarrierId: undefined,
}

export interface INewCreatedDriver {
  firstName: string
  lastName: string
  email?: string
  cellPhone?: string
  motorCarrierId?: number
}

export const EMPTY_DRIVER: Partial<IDriver> = {
  id: undefined,
  firstName: undefined,
  lastName: undefined,
}

export const Driver = memo<IDriverProps>(
  ({
    errors: errorsProp,
    isAddEntityEnabled = true,
    isExternal = false,
    large: largeProp = true,
    layout: layoutProp = 'vertical',
    onChange: onChangeProp,
    refetchOptions,
    required: requiredProp = false,
    selectedMotorCarrier,
    value,
  }) => {
    const { trackEvent } = useFMSAnalytics()
    const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
    const { createTeamMember } = useTeamMembersApi()
    // Props
    const { drivers: driversProp = [], motorCarrierId: motorCarrierIdProp } = value || {}
    const carriers = useSelector(selectCarriersList)

    // Copy drivers to local state
    const [drivers, setDrivers] = useState<IDriver[]>([...driversProp])
    const [firstDriver, secondDriver]: IDriver[] = drivers || []
    const [hasAccountConflict, setHasAccountConflict] = useState<boolean>(false)

    /**
     * Set the initial state only if drivers is empty
     */
    useEffect(() => {
      if (driversProp) {
        setDrivers(driversProp)
      }
    }, [driversProp])

    // Driver Form
    const [isDriverFormVisible, setDriverFormVisible] = useState(false)
    const [currentDriverFormNumber, setCurrentDriverFormNumber] = useState(0)
    const [newCreatedDriver, setNewCreatedDriver] = useState<INewCreatedDriver>(
      defaultNewDriverState
    )

    /**
     * Using a unique id here to trigger a re-initialization
     * of <DriverAutosuggest /> when form is canceled. This is to keep the state uncontrolled
     * inside of the component, but still init state from parent component with default state values
     */
    const { key: firstKey, resetKey: resetFirstKey } = useRefresh(firstDriver?.id)
    const { key: secondKey, resetKey: resetSecondKey } = useRefresh(secondDriver?.id)

    // Errors
    const [driverValidationErrors, setDriverValidationErrors] = useState<string>()

    const { length: driverCount = 0 } = drivers || []
    const [isSecondDriverVisible, setSecondDriverVisible] = useState(driverCount > 1)
    const {
      errors: driverFormErrors,
      setHasErrors: setHasDriverFormErrors,
    } = useValidationFormContext()

    const dispatch = useDispatch()

    // Update local state immediately, and then afterwards, update parent via prop
    const [debouncedOnChange] = useDebouncedCallback(() => {
      const update: Partial<ILoad> = {
        drivers,
      }

      // Look for default motorCarrierId for load
      const [driver1] = drivers || {}
      const { motorCarrierId: newMotorCarrierId } = driver1 || {}
      const motorCarrier = find(carriers, { motorCarrierId: newMotorCarrierId })

      // Only add if set
      if (motorCarrier) {
        update.motorCarrierId = motorCarrier.motorCarrierId
        update.motorCarrier = motorCarrier
      }

      onChangeProp(update)
    }, 10)

    /**
     * Updates local state
     */
    const onChange = useCallback(
      (change: Partial<ILoad>): void => {
        const { drivers: driversChange = [] } = change || {}
        setDrivers(driversChange)
        debouncedOnChange()
      },
      [debouncedOnChange]
    )

    const { driver: newDriver, fetching: newDriverFetching } = useSelector(driverStateSelector)

    // Manages form state
    const driverFormChangeHandler = useCallback((change: Partial<IDriver>) => {
      setNewCreatedDriver(current => ({
        ...current,
        ...change,
      }))
    }, [])

    /**
     * Update local state with changes
     */
    const driverChangeHandler = useCallback(
      (change: IDriver | undefined, formIndex: number) => {
        const updatedDrivers = [...drivers]

        // If change is defined
        if (change) {
          updatedDrivers[formIndex - 1] = change
        } else {
          updatedDrivers.splice(formIndex - 1, 1)
        }

        const update: Partial<ILoad> = { drivers: updatedDrivers }

        // If this hasn't been set, use the driver's motorCarrierId
        if (change?.motorCarrierId && !motorCarrierIdProp) {
          update.motorCarrierId = change?.motorCarrierId
        }

        onChange(update)
      },
      [drivers, motorCarrierIdProp, onChange]
    )

    const removeSecondDriver = () => {
      onChange({ drivers: [firstDriver!] })
    }

    const secondDriverToggleHandler = () => {
      setSecondDriverVisible(!isSecondDriverVisible)

      if (secondDriver && isSecondDriverVisible) {
        removeSecondDriver()
      }
    }

    /**
     * Handle `cancel` button click in <AddNewDriverForm />
     */
    const cancelAddNewDriverHandler = useCallback(
      (index: number) => {
        setDriverFormVisible(false)
        setNewCreatedDriver(defaultNewDriverState)
        driverChangeHandler(undefined, index)
        setHasDriverFormErrors(false)
      },
      [driverChangeHandler]
    )

    const cancelAddFirstDriver = useCallback(() => {
      cancelAddNewDriverHandler(1)
      resetFirstKey()
    }, [cancelAddNewDriverHandler, resetFirstKey])

    const cancelAddSecondDriver = useCallback(() => {
      cancelAddNewDriverHandler(2)
      resetSecondKey()
    }, [cancelAddNewDriverHandler, resetSecondKey])

    /**
     * Create a new driver using form input
     */
    const addNewDriverHandler = useCallback(
      async (d: Partial<IDriver>, i: number) => {
        const payload: RecursivePartial<ITeamMember> = {
          firstName: d.firstName,
          lastName: d.lastName,
          cellPhone: d.cellPhone,
          phone: d.phone,
          email: d.email,
          smsStatusUpdatesEnabled: !!d.settings?.smsStatusUpdatesEnabled,
          motorCarrierDetails: [
            {
              role: TEAM_MEMBER_ROLE_TYPES.EMPLOYEE,
              userType: TEAM_MEMBER_USER_TYPES.DRIVER,
              motorCarrierId: d.motorCarrierId,
            },
          ],
        }
        let createdTeamMember: ITeamMember | undefined
        setHasAccountConflict(false)
        try {
          createdTeamMember = await createTeamMember(payload)
          const { drivers: fetchedDrivers = [] } = (await Api.fetchAllDrivers()) || {}
          const driver = fetchedDrivers.find(
            fetchedDriver => fetchedDriver.transfixUserId === createdTeamMember?.id
          )
          // We need to update Redux store with latest results
          // otherwise the driver selection won't match up with
          // latest data
          dispatch(fetchDriversSuccess(fetchedDrivers))
          driverChangeHandler(driver, i)
          setDriverFormVisible(false)
          setHasDriverFormErrors(false)
        } catch (e) {
          if (e === 'Error: email and cellphone match different existing accounts') {
            setHasAccountConflict(true)
          } else {
            setDriverValidationErrors(e)
          }
        }
        if (createdTeamMember) {
          const { firstName = '', lastName = '' } = createdTeamMember || {}
          const fullNameString = firstName && lastName ? ` (“${firstName} ${lastName}”)` : ''
          openToast({
            toastType: 'default',
            toastMessage: createdTeamMember.isExternal
              ? `Your driver already has a Transfix account${fullNameString}. They’ve been added to your carrier.`
              : 'Driver added.',
          })
        }
      },
      [createTeamMember, dispatch, driverChangeHandler]
    )

    useEffect(() => {
      if (newDriver?.id) setDriverFormVisible(false)
    }, [newDriver])

    const showUnAssignedSuggestion = !secondDriver

    const getNamesFromInput = (input?: string): { firstName: string; lastName: string } => {
      if (!input || !input.length || !input.split(' ').length) {
        return {
          firstName: '',
          lastName: '',
        }
      }

      const [firstName, lastName] = input.split(' ') || []
      return {
        firstName: capitalize(firstName),
        lastName: capitalize(lastName),
      }
    }

    /**
     * Handle changes from <DriverAutosuggest />
     */
    const onChangeDriverForm = useCallback(
      (isFormVisible: boolean, formIndex: number, name?: string) => {
        setDriverFormVisible(isFormVisible)
        setCurrentDriverFormNumber(formIndex)

        // If true, set values to be automatically incuded
        if (isFormVisible) {
          const { firstName, lastName } = getNamesFromInput(name)
          driverFormChangeHandler({
            firstName,
            lastName,
          })
        }
      },
      [driverFormChangeHandler]
    )

    const addFirstDriverHandler = () => {
      onChangeDriverForm(true, 1, undefined)
    }

    const addSecondDriverHandler = () => {
      onChangeDriverForm(true, 2, undefined)
    }

    const toggleShowHandler = () => {
      setSecondDriverVisible(false)
    }

    const addNewFirstDriverHandler = (driver: DriverSchemaType) => {
      const req = createDriverRequest(driver)
      addNewDriverHandler(req, 1)
    }

    const addNewSecondDriverHandler = (driver: DriverSchemaType) => {
      const req = createDriverRequest(driver)
      addNewDriverHandler(req, 2)
    }

    const driversBulkUploadClickHandler = () =>
      trackEvent('Bulk Upload', 'CLICK_DRIVERS_BULK_UPLOAD_LINK')

    const blockWidth = layoutProp === 'horizontal' ? 6 : 12

    return (
      <>
        <Grid container>
          <Grid item xs={12}>
            <Box mb={layoutProp === 'horizontal' ? 0 : 2}>
              <Grid container spacing={2}>
                <Grid item xs={blockWidth}>
                  <DriverAutosuggest
                    key={firstKey}
                    large={largeProp}
                    formIndex={1}
                    showUnassignedSuggestion={showUnAssignedSuggestion}
                    label={isSecondDriverVisible ? 'Driver 1' : 'Driver'}
                    shouldFetchList={refetchOptions}
                    defaultValue={firstDriver || UNASSIGNED_VALUE}
                    currentDrivers={drivers}
                    setDriverForm={onChangeDriverForm}
                    onChange={driverChangeHandler}
                    onAddAnotherDriver={secondDriverToggleHandler}
                    isAddButtonVisible={!isSecondDriverVisible}
                    disabled={isDriverFormVisible && !isSecondDriverVisible}
                    error={errorsProp}
                    hideAllButtons={layoutProp === 'horizontal'}
                    required={requiredProp}
                    isAddOptionVisible={isAddEntityEnabled}
                    selectedMotorCarrierId={selectedMotorCarrier?.id}
                  />
                  {isAddEntityEnabled && !isDriverFormVisible && !firstDriver?.id && (
                    <Button
                      className='btn-text-link-b mt1'
                      data-test='add-new-driver-cta'
                      onClick={addFirstDriverHandler}>
                      + Add new driver
                    </Button>
                  )}
                  {isDriverFormVisible && currentDriverFormNumber === 1 && (
                    <AddEntityModal
                      open
                      title='Add new driver'
                      error={driverValidationErrors || driverFormErrors}
                      onClose={cancelAddFirstDriver}>
                      <InlineMessage
                        className='mb5 mt3 mt0-m'
                        message={
                          <span>
                            <b>Lots of drivers to add?</b>{' '}
                            <a
                              href={`mailto:${FP_SUPPORT_EMAIL}`}
                              onClick={driversBulkUploadClickHandler}>
                              Contact us
                            </a>{' '}
                            and we’ll import your drivers in bulk.
                          </span>
                        }
                      />
                      <DriverForm
                        isExternal={isExternal}
                        defaultValues={{
                          firstName: newCreatedDriver.firstName,
                          lastName: newCreatedDriver.lastName,
                          motorCarrier: selectedMotorCarrier,
                        }}
                        onSuccess={addNewFirstDriverHandler}
                        onCancel={cancelAddFirstDriver}
                        hasAccountConflict={hasAccountConflict}
                      />
                    </AddEntityModal>
                  )}
                </Grid>
                {isSecondDriverVisible && (
                  <Grid item xs={blockWidth}>
                    {isSecondDriverVisible && (
                      <Box mt={layoutProp === 'vertical' ? 2 : 0}>
                        <DriverAutosuggest
                          key={secondKey}
                          showUnassignedSuggestion={false}
                          label='Driver 2'
                          defaultValue={secondDriver || UNASSIGNED_VALUE}
                          onChange={driverChangeHandler}
                          shouldFetchList={false}
                          large={largeProp}
                          formIndex={2}
                          setDriverForm={onChangeDriverForm}
                          currentDrivers={[firstDriver]}
                          isRemoveButtonVisible
                          disabled={isDriverFormVisible && isSecondDriverVisible}
                          onToggleShow={toggleShowHandler}
                          error={errorsProp}
                          isAddOptionVisible={isAddEntityEnabled}
                          selectedMotorCarrierId={selectedMotorCarrier?.id}
                        />
                      </Box>
                    )}
                    {isAddEntityEnabled &&
                      !isDriverFormVisible &&
                      !secondDriver?.id &&
                      isSecondDriverVisible && (
                        <Button
                          className='btn-text-link-b mt1'
                          data-test='add-new-driver-cta'
                          onClick={addSecondDriverHandler}>
                          + Add new driver
                        </Button>
                      )}
                    {isDriverFormVisible && currentDriverFormNumber === 2 && !newDriverFetching && (
                      <AddEntityModal
                        open
                        title='Add New Driver'
                        error={driverValidationErrors}
                        onClose={cancelAddFirstDriver}>
                        <DriverForm
                          isExternal={isExternal}
                          defaultValues={{
                            firstName: newCreatedDriver.firstName,
                            lastName: newCreatedDriver.lastName,
                            email: newCreatedDriver.email,
                            cellPhone: newCreatedDriver.cellPhone,
                          }}
                          onSuccess={addNewSecondDriverHandler}
                          onCancel={cancelAddSecondDriver}
                        />
                      </AddEntityModal>
                    )}
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
        <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      </>
    )
  }
)
